import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getCSRFToken } from 'lib/request';
import Button from 'components/styleguide/button/Button';
import SidePanel from 'components/styleguide/side_panel/SidePanel';
import EorBreakdown from './eor_breakdown';
import { EstimateData, Package } from './eor_calculator_types';

const styles = require('./eor_calculator.module.scss');
const iconCheckPurple = require('svg/icons-lj-styleguide-2025/check-purple.svg');
const iconCheckboxDefault = require('svg/icons-lj-styleguide-2025/checkbox-default.svg');
const iconCheckboxSelected = require('svg/icons-lj-styleguide-2025/checkbox-selected.svg');

export interface EorCalculatorProps {
  packagesPath: string;
  estimatePath: string;
  quotePdfPath: string;
  eorServiceUrl?: string;
}

const ICON_CHECK = <img className={styles.iconCheck} src={iconCheckPurple} alt="" width="16" height="16" />;

const ICON_CLOSE = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ICON_WARNING = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 6V8.66667M8 11.3333H8.00667M14.4866 12L9.15329 2.66665C9.037 2.46146 8.86836 2.29078 8.66457 2.17203C8.46078 2.05329 8.22915 1.99072 7.99329 1.99072C7.75743 1.99072 7.52579 2.05329 7.322 2.17203C7.11822 2.29078 6.94958 2.46146 6.83329 2.66665L1.49995 12C1.38241 12.2036 1.32077 12.4346 1.32129 12.6697C1.32181 12.9047 1.38447 13.1355 1.50292 13.3385C1.62136 13.5416 1.79138 13.7097 1.99575 13.8259C2.20011 13.942 2.43156 14.0021 2.66662 14H13.3333C13.5672 13.9997 13.797 13.938 13.9995 13.8208C14.202 13.7037 14.3701 13.5354 14.487 13.3327C14.6038 13.1301 14.6653 12.9002 14.6653 12.6663C14.6652 12.4324 14.6036 12.2026 14.4866 12Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MIN_SALARY = 20_000;
const MAX_SALARY = 150_000;

function parseSalary(formatted: string): number {
  return parseInt(formatted.replace(/[^\d]/g, ''), 10) || 0;
}

function serializeAddons(addons: Set<string>): string {
  return Array.from(addons).join(',');
}

export default function EorCalculator({ packagesPath, estimatePath, quotePdfPath, eorServiceUrl = 'https://landing.jobs/business-employer-of-record/' }: EorCalculatorProps) {
  const [visible, setVisible] = useState(false);
  const [salary, setSalary] = useState('');
  const [sliderValue, setSliderValue] = useState(MIN_SALARY);
  const [error, setError] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedAddons, setSelectedAddons] = useState<Set<string>>(new Set());
  const [isMonthly, setIsMonthly] = useState(true);
  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [feeExpanded, setFeeExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [expandedAddons, setExpandedAddons] = useState<Set<string>>(new Set());
  const fetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchEstimateRef = useRef<typeof fetchEstimate>(null as any);
  const sliderRef = useRef<HTMLInputElement>(null);

  const open = useCallback(() => {
    setVisible(true);
    if (packages.length === 0) fetchPackages();
  }, [packages.length]);

  const close = useCallback(() => setVisible(false), []);

  useEffect(() => {
    const handler = () => open();
    document.addEventListener('eor-calculator:open', handler);
    return () => document.removeEventListener('eor-calculator:open', handler);
  }, [open]);

  useEffect(() => {
    return () => {
      if (fetchTimer.current) clearTimeout(fetchTimer.current);
    };
  }, []);

  const fetchPackages = () => {
    fetch(packagesPath, { headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCSRFToken() } })
      .then(res => res.json())
      .then(data => {
        if (data.packages?.length > 0) {
          setPackages(data.packages);
          setSelectedPackage(data.packages[0].code);
        }
      })
      .catch(() => { /* fall back to tailored-only option */ });
  };

  const fetchEstimate = useCallback((sal?: string, pkg?: string, addons?: Set<string>, monthly?: boolean) => {
    const salaryVal = sal ?? salary;
    const pkgCode = pkg ?? selectedPackage;
    const addonSet = addons ?? selectedAddons;
    const period = (monthly ?? isMonthly) ? 'monthly' : 'yearly';
    const salaryNum = parseSalary(salaryVal);
    if (salaryNum === 0 || pkgCode === 'tailored' || !pkgCode) return;

    const addonCodes = serializeAddons(addonSet);
    const url = `${estimatePath}?annual_salary=${salaryNum}&package_code=${encodeURIComponent(pkgCode)}&addons=${encodeURIComponent(addonCodes)}&period=${period}`;

    setLoading(true);
    setEstimateError(false);
    fetch(url, { headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCSRFToken() } })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data.error) {
          setEstimateError(true);
          return;
        }
        setEstimate(data);
      })
      .catch(() => setEstimateError(true))
      .finally(() => setLoading(false));
  }, [salary, selectedPackage, selectedAddons, isMonthly, estimatePath]);

  useEffect(() => {
    fetchEstimateRef.current = fetchEstimate;
  }, [fetchEstimate]);

  const debouncedFetch = useCallback((...args: Parameters<typeof fetchEstimate>) => {
    if (fetchTimer.current) clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(() => fetchEstimateRef.current(...args), 300);
  }, []);

  const cancelPendingFetch = useCallback(() => {
    if (fetchTimer.current) clearTimeout(fetchTimer.current);
  }, []);

  const updateSliderBackground = (val: number) => {
    if (!sliderRef.current) return;
    const pct = ((val - MIN_SALARY) / (MAX_SALARY - MIN_SALARY)) * 100;
    const active = getComputedStyle(sliderRef.current).getPropertyValue('--slider-active').trim();
    const track = getComputedStyle(sliderRef.current).getPropertyValue('--slider-track').trim();
    sliderRef.current.style.background = `linear-gradient(to right, ${active} ${pct}%, ${track} ${pct}%)`;
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    setSliderValue(val);
    const formatted = val.toLocaleString('en-IE');
    setSalary(formatted);
    setError(false);
    updateSliderBackground(val);
    if (showBreakdown) debouncedFetch(formatted);
  };

  const handleSalaryInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    const num = parseInt(raw, 10) || 0;
    setSalary(raw ? num.toLocaleString('en-IE') : '');
    setSliderValue(Math.max(MIN_SALARY, Math.min(num, MAX_SALARY)));
    setError(false);
    updateSliderBackground(Math.max(MIN_SALARY, Math.min(num, MAX_SALARY)));
    if (showBreakdown) debouncedFetch(raw ? num.toLocaleString('en-IE') : '');
  };

  const handleCalculate = () => {
    if (parseSalary(salary) === 0) {
      setError(true);
      return;
    }
    setError(false);
    setShowBreakdown(true);
    if (packages.length === 0) fetchPackages();
    cancelPendingFetch();
    fetchEstimate();
  };

  const handlePackageChange = (code: string) => {
    setSelectedPackage(code);
    setSelectedAddons(new Set());
    if (code !== 'tailored') debouncedFetch(undefined, code, new Set());
  };

  const handleAddonToggle = (code: string) => {
    setSelectedAddons(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      debouncedFetch(undefined, undefined, next);
      return next;
    });
  };

  const handleDownloadQuote = () => {
    const url = `${quotePdfPath}?annual_salary=${parseSalary(salary)}&package_code=${encodeURIComponent(selectedPackage)}&addons=${encodeURIComponent(serializeAddons(selectedAddons))}`;
    setPdfError(false);
    setPdfLoading(true);
    fetch(url, { headers: { 'X-CSRF-Token': getCSRFToken() } })
      .then(res => {
        if (!res.ok) throw new Error('PDF unavailable');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
      })
      .catch(() => setPdfError(true))
      .finally(() => setPdfLoading(false));
  };

  const handlePeriodToggle = () => {
    const next = !isMonthly;
    setIsMonthly(next);
    debouncedFetch(undefined, undefined, undefined, next);
  };

  useEffect(() => {
    updateSliderBackground(sliderValue);
  }, [visible, sliderValue]);

  return (
    <SidePanel visible={visible} onClose={close} labelledBy="eor-calculator-title" panelClassName={styles.panel}>
      <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>Calculate Employee of Record cost</h2>
            <div className={styles.subtitle}>
              <span>Know more about our</span>
              <Button context="company" type="terceary" size="S" href={eorServiceUrl} target="_blank" iconRight="squareArrowOutUpRight">
                EOR service
              </Button>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={close} aria-label="Close calculator">{ICON_CLOSE}</button>
        </div>

        <div className={styles.body}>
          <label className={styles.label} htmlFor="eor-salary-input">Annual gross salary</label>
          <div className={`${styles.inputWrapper} ${error ? styles.inputWrapperError : ''}`}>
            <input
              id="eor-salary-input"
              className={styles.input}
              type="text"
              placeholder="Enter employee salary"
              value={salary}
              onChange={handleSalaryInput}
              aria-invalid={error}
              aria-describedby={error ? 'eor-salary-error' : undefined}
            />
            <span className={styles.currency}>€</span>
          </div>
          {error && (
            <div id="eor-salary-error" className={styles.error} role="alert">
              {ICON_WARNING}
              <span>Please enter employee salary to calculate EOR</span>
            </div>
          )}
          <input
            ref={sliderRef}
            className={styles.slider}
            type="range"
            min={MIN_SALARY}
            max={MAX_SALARY}
            step="5000"
            value={sliderValue}
            onChange={handleSliderChange}
            aria-label="Annual gross salary"
          />

          {showBreakdown && (
            <>
              <div className={styles.toggle}>
                <span className={`${styles.toggleLabel} ${!isMonthly ? styles.toggleLabelActive : ''}`}>Yearly</span>
                <label className={styles.toggleSwitch}>
                  <input type="checkbox" checked={isMonthly} onChange={handlePeriodToggle} aria-label="Toggle monthly or yearly period" />
                  <span className={styles.toggleSlider} />
                </label>
                <span className={`${styles.toggleLabel} ${isMonthly ? styles.toggleLabelActive : ''}`}>Monthly</span>
              </div>

              {packages.map(pkg => (
                <div
                  key={pkg.code}
                  className={`${styles.packageCard} ${selectedPackage === pkg.code ? styles.packageCardSelected : ''}`}
                >
                  <label className={styles.packageHeader}>
                    <input
                      type="radio"
                      name="eor-package"
                      value={pkg.code}
                      checked={selectedPackage === pkg.code}
                      onChange={() => handlePackageChange(pkg.code)}
                      className={styles.packageRadio}
                    />
                    <span className={styles.packageRadioCustom} />
                    <div className={styles.packageInfo}>
                      <span className={styles.packageName}>{pkg.name}</span>
                      {pkg.description && <span className={styles.packageDesc}>{pkg.description}</span>}
                    </div>
                    <span className={styles.packagePrice}>
                      {Math.round(pkg.monthlyFee * (isMonthly ? 1 : 12))}€<span className={styles.packagePriceSuffix}> fee</span>
                    </span>
                  </label>

                  {selectedPackage === pkg.code && pkg.addons?.length > 0 && (() => {
                    const defaultAddons = pkg.addons.filter(a => a.addonType === 'DEFAULT');
                    const extraAddons = pkg.addons.filter(a => a.addonType !== 'DEFAULT');
                    const allAddons = [...defaultAddons, ...extraAddons];
                    const isExpanded = expandedAddons.has(pkg.code);
                    const visibleAddons = isExpanded ? allAddons : allAddons.slice(0, 5);
                    const hasMore = allAddons.length > 5;
                    return (
                    <div className={styles.packageBody}>
                      {visibleAddons.map(addon => (
                        addon.addonType === 'DEFAULT' ? (
                          <div key={addon.code} className={styles.careIncludedItem}>
                            {ICON_CHECK} {addon.name}
                          </div>
                        ) : (
                          <div
                            key={addon.code}
                            className={styles.addonItem}
                            role="checkbox"
                            aria-checked={selectedAddons.has(addon.code)}
                            tabIndex={0}
                            onClick={() => handleAddonToggle(addon.code)}
                            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleAddonToggle(addon.code); } }}
                          >
                            <img
                              className={styles.addonCheckboxCustom}
                              src={selectedAddons.has(addon.code) ? iconCheckboxSelected : iconCheckboxDefault}
                              alt=""
                              width="24"
                              height="24"
                            />
                            <span className={styles.addonName}>{addon.name}</span>
                            <span className={styles.addonPrice}>+ {Math.round(addon.monthlyCost * (isMonthly ? 1 : 12))}€</span>
                          </div>
                        )
                      ))}
                      {hasMore && (
                        <button
                          type="button"
                          className={styles.showMoreBtn}
                          onClick={() => setExpandedAddons(prev => {
                            const next = new Set(prev);
                            if (next.has(pkg.code)) next.delete(pkg.code);
                            else next.add(pkg.code);
                            return next;
                          })}
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                          <svg className={`${styles.showMoreChevron} ${isExpanded ? styles.showMoreChevronUp : ''}`} width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" fill="currentColor" />
                          </svg>
                        </button>
                      )}
                    </div>
                    );
                  })()}
                </div>
              ))}

              <div className={`${styles.packageCard} ${selectedPackage === 'tailored' ? styles.packageCardSelected : ''}`}>
                <label className={styles.packageHeader}>
                  <input
                    type="radio"
                    name="eor-package"
                    value="tailored"
                    checked={selectedPackage === 'tailored'}
                    onChange={() => handlePackageChange('tailored')}
                    className={styles.packageRadio}
                  />
                  <span className={styles.packageRadioCustom} />
                  <div className={styles.packageInfo}>
                    <span className={styles.packageName}>TAILORED</span>
                    <span className={styles.packageDesc}>Build to fit your needs</span>
                  </div>
                  <span className={`${styles.packagePrice} ${styles.packagePricePurple}`}>Upon request</span>
                </label>
              </div>

              <div aria-live="polite" aria-atomic="true">
              {estimateError && selectedPackage !== 'tailored' && (
                <div className={styles.error} role="alert">
                  {ICON_WARNING}
                  <span>Could not update pricing, please try again.</span>
                </div>
              )}
              {estimate && selectedPackage !== 'tailored' && (
                <EorBreakdown
                  estimate={estimate}
                  packages={packages}
                  selectedPackage={selectedPackage}
                  selectedAddons={selectedAddons}
                  isMonthly={isMonthly}
                  feeExpanded={feeExpanded}
                  onToggleFee={() => setFeeExpanded(!feeExpanded)}
                />
              )}
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          {!showBreakdown ? (
            <Button
              context="company"
              type="primary"
              size="M"
              onClick={handleCalculate}
              disabled={loading}
              spinner={loading}
              className={styles.fullWidth}
            >
              Calculate
            </Button>
          ) : (
            <div className={styles.footerActions}>
              <Button context="company" type="primary" size="M" href="https://campaign.landing.jobs/business-eor-meeting" target="_blank" className={styles.fullWidth}>
                Request a meeting
              </Button>
              {selectedPackage !== 'tailored' && (
                <Button
                  context="company"
                  type="terceary"
                  size="M"
                  onClick={handleDownloadQuote}
                  disabled={pdfLoading}
                  spinner={pdfLoading}
                  iconRight="squareArrowOutUpRight"
                >
                  Get full report
                </Button>
              )}
            </div>
          )}
          {pdfError && (
            <div className={styles.error} role="alert">
              {ICON_WARNING}
              <span>Report temporarily unavailable, please try again.</span>
            </div>
          )}
        </div>
    </SidePanel>
  );
}
